import { SensorType } from '@/interfaces/enums'

export interface IManagementModule {
  id: number
  title: string
  enabled: boolean
  default_window_days: number
  favorite_area_id: number | null
  show_favorite_overview: boolean
  company_id: number
}

export interface IAreaSensor {
  id: number
  sensor_id: number
  position: number
  indicators: string[]
  sensor_name: string
  sensor_type: SensorType
  allowed_indicators: string[]
}

export interface IManagementArea {
  id: number
  name: string
  position: number
  parent_id: number | null
  management_module_id: number
  company_id: number
  children: IManagementArea[]
  management_area_sensors: IAreaSensor[]
}

export interface IEnergyTariff {
  id: number
  name: string
  rate_peak_consumption: number
  rate_offpeak_consumption: number
  rate_peak_demand: number
  rate_offpeak_demand: number
  valid_from: string
  valid_until: string | null
}

export interface IEnergyCost {
  tariff_id: number
  cost_peak_consumption: number
  cost_offpeak_consumption: number
  cost_peak_demand: number
  cost_offpeak_demand: number
  cost_total: number
}

export interface ISensorIndicatorResult {
  management_area_sensor_id: number
  sensor_id: number
  sensor_name: string
  sensor_type: string
  configured_indicators: string[]
  values: Record<string, number | IEnergyCost | null>
}

export interface IAreaAlarmStat {
  alarm_type: string
  count: number
  open_count: number
  avg_duration_minutes: number | null
}

export interface IAreaClassificationStat {
  classification: string
  count: number
  stopped_minutes: number
}

// --- Consolidado mensal (relatórios empilhados, colunas configuráveis) ---

export interface IConsolidatedMetricMeta {
  key: string
  label: string
  unit: string
  group: string
  agg: 'sum' | 'avg'
}

export const CONSOLIDATED_METRICS: IConsolidatedMetricMeta[] = [
  { key: 'consumo_total', label: 'Consumo total', unit: 'kWh', group: 'Consumo', agg: 'sum' },
  { key: 'consumo_ponta', label: 'Consumo ponta', unit: 'kWh', group: 'Consumo', agg: 'sum' },
  { key: 'consumo_fora_ponta', label: 'Consumo fora ponta', unit: 'kWh', group: 'Consumo', agg: 'sum' },
  { key: 'demanda_ponta', label: 'Demanda ponta', unit: 'kW', group: 'Demanda', agg: 'avg' },
  { key: 'demanda_fora_ponta', label: 'Demanda fora ponta', unit: 'kW', group: 'Demanda', agg: 'avg' },
  { key: 'custo_total', label: 'Custo total', unit: 'R$', group: 'Custo', agg: 'sum' },
  { key: 'custo_ponta', label: 'Custo ponta', unit: 'R$', group: 'Custo', agg: 'sum' },
  { key: 'custo_fora_ponta', label: 'Custo fora ponta', unit: 'R$', group: 'Custo', agg: 'sum' },
  { key: 'temperatura_media', label: 'Temperatura média', unit: '°C', group: 'Operação', agg: 'avg' },
  { key: 'utilizacao', label: 'Utilização', unit: '%', group: 'Operação', agg: 'avg' },
  { key: 'paradas', label: 'Qtd de paradas', unit: '', group: 'Operação', agg: 'sum' },
  { key: 'tempo_parada', label: 'Tempo de parada', unit: 'min', group: 'Operação', agg: 'sum' },
  { key: 'demais_alarmes', label: 'Demais alarmes', unit: '', group: 'Operação', agg: 'sum' },
  { key: 'alarmes', label: 'Alarmes', unit: '', group: 'Operação', agg: 'sum' },
]

export function consolidatedMetricMeta(key: string): IConsolidatedMetricMeta | undefined {
  return CONSOLIDATED_METRICS.find((m) => m.key === key)
}

// Colunas fixas da Visão consolidada (tabelas mensais por área/linha — igual à planilha).
export const CONSOLIDATED_AREA_METRICS = ['utilizacao', 'tempo_parada', 'paradas', 'demais_alarmes', 'consumo_total']

export interface IConsolidatedReport {
  id: string
  areaId: number
  areaLabel: string
  sensorId: number | null
  sensorLabel: string | null
  monthsBack: number
  metrics: string[]
}

export interface IMonthConsolidation {
  month: string
  complete: boolean
  sensors_count: number
  values: Record<string, number | null>
}

export interface IAreaIndicators {
  area_id: number
  area_name: string
  start_date: string
  end_date: string
  tariff_id: number | null
  sensors: ISensorIndicatorResult[]
  totals: Record<string, number | null>
  alarms: IAreaAlarmStat[]
  alarm_classifications: IAreaClassificationStat[]
  // Indicadores operacionais (para o Resumo Gerencial do relatório).
  door_open_ranking?: IDoorOpenRank[]
  tempo_total_porta_aberta?: number | null
  temperatures?: ITemperatureRank[]
  machine_runtime?: IMachineRuntime[]
}

/** Ranking de porta aberta por sensor (tempo total e nº de eventos). */
export interface IDoorOpenRank {
  sensor_id: number
  name: string
  total_minutes: number
  eventos: number
}

/** Temperatura média por sensor de ambiente. */
export interface ITemperatureRank {
  sensor_id: number
  name: string
  temperatura_media: number
}

/** Tempo de máquina ligada/desligada por sensor (a partir dos alarmes de interrupção). */
export interface IMachineRuntime {
  sensor_id: number
  name: string
  on_minutes: number
  off_minutes: number
  paradas: number
}

/** Ponto diário do gráfico "tempo de parada x tempo rodando". */
export interface IDailyRuntimePoint {
  date: string // 'YYYY-MM-DD'
  on_minutes: number
  off_minutes: number
  paradas: number
}

export interface IDailyRuntime {
  area_id: number
  area_name: string
  start_date: string
  end_date: string
  days: IDailyRuntimePoint[]
}

/** Linha da Visão consolidada (1 por área/linha de produção no período). */
export interface IConsolidatedAreaRow {
  utilizacao: number | null
  tempo_parada_min: number
  paradas: number
  demais_alarmes: number
  consumo_total: number | null
}

/**
 * Deriva as métricas operacionais consolidadas (utilização/paradas/alarmes/consumo)
 * de uma área a partir dos indicadores já calculados.
 */
export function deriveConsolidatedRow(indicators: IAreaIndicators): IConsolidatedAreaRow {
  const runtime = indicators.machine_runtime ?? []
  const tempoParadaMin = runtime.reduce((sum, r) => sum + (r.off_minutes ?? 0), 0)
  const paradas = runtime.reduce((sum, r) => sum + (r.paradas ?? 0), 0)
  const totalAlarmes = (indicators.alarms ?? []).reduce((sum, a) => sum + a.count, 0)
  const interrupcoes = (indicators.alarms ?? [])
    .filter((a) => a.alarm_type === 'interruption')
    .reduce((sum, a) => sum + a.count, 0)
  return {
    utilizacao: indicators.totals?.utilizacao_media ?? null,
    tempo_parada_min: tempoParadaMin,
    paradas,
    demais_alarmes: Math.max(totalAlarmes - interrupcoes, 0),
    consumo_total: indicators.totals?.consumo_total ?? null,
  }
}

/** Meta (limite ideal) de quantidade de alarmes por tipo. */
export interface IAlarmTarget {
  id?: number
  alarm_type: string
  target_count: number
}

export interface IIndicatorMeta {
  key: string
  label: string
  unit: string
}

/** Indicadores disponíveis por tipo de sensor — guia o picker e a exibição dos cards. */
export const INDICATORS_BY_TYPE: Record<string, IIndicatorMeta[]> = {
  [SensorType.ELECTRICITY]: [
    { key: 'consumo_total', label: 'Consumo total', unit: 'kWh' },
    { key: 'consumo_ponta', label: 'Consumo ponta', unit: 'kWh' },
    { key: 'consumo_fora_ponta', label: 'Consumo fora ponta', unit: 'kWh' },
    { key: 'demanda_ponta', label: 'Demanda ponta', unit: 'kW' },
    { key: 'demanda_fora_ponta', label: 'Demanda fora ponta', unit: 'kW' },
    { key: 'utilizacao', label: 'Utilização', unit: '%' },
  ],
  [SensorType.TEMPERATURE]: [{ key: 'temperatura_media', label: 'Temperatura média', unit: '°C' }],
  [SensorType.DOOR_OPEN]: [{ key: 'tempo_medio_porta_aberta', label: 'Tempo médio porta aberta', unit: 'min' }],
  [SensorType.ROTATION]: [],
}

export function indicatorsForType(sensorType: string): IIndicatorMeta[] {
  return INDICATORS_BY_TYPE[sensorType] ?? []
}

export function indicatorMeta(key: string): IIndicatorMeta | undefined {
  return Object.values(INDICATORS_BY_TYPE)
    .flat()
    .find((indicator) => indicator.key === key)
}

/** Agrupamento dos indicadores para exibição (Consumo / Demanda / Operação / Custo). */
export const INDICATOR_GROUPS: { key: string; label: string; keys: string[] }[] = [
  { key: 'consumo', label: 'Consumo', keys: ['consumo_total', 'consumo_ponta', 'consumo_fora_ponta'] },
  { key: 'demanda', label: 'Demanda', keys: ['demanda_ponta', 'demanda_fora_ponta'] },
  {
    key: 'operacao',
    label: 'Operação',
    keys: ['utilizacao', 'utilizacao_media', 'temperatura_media', 'tempo_medio_porta_aberta'],
  },
]

export interface IIndicatorCard {
  key: string
  label: string
  value: number | null
  unit: string
}

/** Agrupa uma lista de cards conforme INDICATOR_GROUPS, descartando grupos vazios. */
export function groupIndicatorCards(cards: IIndicatorCard[]): { label: string; cards: IIndicatorCard[] }[] {
  return INDICATOR_GROUPS.map((group) => ({
    label: group.label,
    cards: cards.filter((card) => group.keys.includes(card.key)),
  })).filter((group) => group.cards.length > 0)
}

// ===== PREVISTO (forecast) vs REALIZADO =====
export interface IForecastMonth {
  month: number // 1..12
  consumo_ponta: number | null
  consumo_fora_ponta: number | null
  consumo_total: number | null
}

export interface IForecastTarget {
  forecastable_type: 'ManagementArea' | 'ManagementAreaSensor'
  forecastable_id: number
  sensor_id?: number
  sensor_name?: string | null
  label?: string
  months: IForecastMonth[]
}

export interface IForecastConsistency {
  month: number
  area_total: number | null
  sensors_total: number | null
  diff: number | null
  ok: boolean
}

export interface IForecastGrid {
  year: number
  area: IForecastTarget
  sensors: IForecastTarget[]
  consistency: IForecastConsistency[]
}

export interface IForecastVsActualPoint {
  month: string // 'YYYY-MM'
  label?: string
  previsto: number | null
  realizado: number | null
  complete?: boolean
}

export interface IForecastVsActual {
  year: number
  months: IForecastVsActualPoint[]
  accumulated: IForecastVsActualPoint[]
  totals: { previsto: number | null; realizado: number | null }
}

// Desdobramento por sensor (previsto x realizado) de um mês — tabela do PDF.
export interface ISensorBreakdownRow {
  name: string | null
  sensor_id: number
  previsto: { consumo_ponta: number | null; consumo_fora_ponta: number | null }
  realizado: Record<string, number | null>
}
export interface ISensorBreakdown {
  month: string
  rows: ISensorBreakdownRow[]
}

// Payload do PUT forecasts (grade do ano).
export interface IForecastSaveRow {
  month: number
  consumo_ponta: number | null
  consumo_fora_ponta: number | null
}
export interface IForecastSavePayload {
  year: number
  area: IForecastSaveRow[]
  sensors: Record<string, IForecastSaveRow[]>
}

/** Rótulos e unidades dos totais agregados da área. */
export const AREA_TOTAL_META: IIndicatorMeta[] = [
  { key: 'consumo_total', label: 'Consumo total', unit: 'kWh' },
  { key: 'consumo_ponta', label: 'Consumo ponta', unit: 'kWh' },
  { key: 'consumo_fora_ponta', label: 'Consumo fora ponta', unit: 'kWh' },
  { key: 'demanda_ponta', label: 'Demanda ponta', unit: 'kW' },
  { key: 'demanda_fora_ponta', label: 'Demanda fora ponta', unit: 'kW' },
  { key: 'utilizacao_media', label: 'Utilização média', unit: '%' },
  { key: 'temperatura_media', label: 'Temperatura média', unit: '°C' },
  { key: 'tempo_medio_porta_aberta', label: 'Tempo médio porta aberta', unit: 'min' },
]
